"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.installAppBaseThemeFollow = exports.APP_BASE_THEME_SHARED = exports.APP_BASE_THEME_VALUES = exports.APP_BASE_THEME_TOKEN_SOURCES = exports.APP_BASE_THEME_MESSAGE = exports.APP_BASE_DESIGN_ATTRIBUTE = exports.APP_BASE_THEME_MODES = exports.APP_BASE_THEME_NAMES = void 0;
exports.renderAppBaseThemeStylesheet = renderAppBaseThemeStylesheet;
exports.applyAppBaseDesign = applyAppBaseDesign;
/**
 * The design of the StreamLain app for App pages that take it: colours and
 * shadows of each theme of the app as `--sl-*` variables, the shared sizes,
 * and the motion tokens. An App that keeps its own design ignores all of it.
 * The values come from the v2 tables `@streamlain/theme` publishes as JSON.
 */
const values_json_1 = __importDefault(require("@streamlain/theme/v2/values.json"));
const app_base_html_document_1 = require("./app-base-html-document");
const app_base_motion_1 = require("./app-base-motion");
/** Names the theme an App page shows on `<html data-theme>`. */
exports.APP_BASE_THEME_NAMES = ['light', 'dark', 'blue-white'];
/** Light or dark: the colour scheme each theme of the app renders in. */
exports.APP_BASE_THEME_MODES = {
    light: 'light',
    dark: 'dark',
    'blue-white': 'dark',
};
/** `<html data-sl-design="streamlain">` marks a page in the StreamLain design. */
exports.APP_BASE_DESIGN_ATTRIBUTE = 'data-sl-design';
/**
 * The host of an App page tells it the theme of its viewer with this message;
 * the page shows it on `<html data-theme>`.
 */
exports.APP_BASE_THEME_MESSAGE = 'streamlain:app-theme/v1';
/** Which value of `@streamlain/theme` v2 each colour and shadow token takes. */
exports.APP_BASE_THEME_TOKEN_SOURCES = {
    '--sl-color-background': 'layer/background/primary',
    '--sl-color-surface': 'layer/background/overlayPanel',
    '--sl-color-surface-muted': 'layer/background/secondary',
    '--sl-color-surface-strong': 'layer/background/tertiary',
    '--sl-color-hover': 'layer/background/hoverOverlay',
    '--sl-color-selected': 'layer/background/selected',
    '--sl-color-border': 'layer/insideBorder/border',
    '--sl-color-focus': 'layer/insideBorder/primaryBorder',
    '--sl-color-text': 'text/primary',
    '--sl-color-text-muted': 'text/secondary',
    '--sl-color-text-subtle': 'text/tertiary',
    '--sl-color-text-disabled': 'text/disable',
    '--sl-color-text-on-accent': 'text/pureWhite',
    '--sl-color-link': 'text/link',
    '--sl-color-icon': 'icon/primary',
    '--sl-color-accent': 'button/primary',
    '--sl-color-danger': 'status/error',
    '--sl-color-danger-surface': 'layer/background/error',
    '--sl-color-success': 'status/success',
    '--sl-color-success-surface': 'layer/background/success',
    '--sl-color-warning': 'status/warning',
    '--sl-color-warning-surface': 'layer/background/warning',
    '--sl-shadow-1': 'shadow/level1',
    '--sl-shadow-2': 'shadow/level2',
    '--sl-shadow-3': 'shadow/level3',
};
/** The value of each token in each theme, read from the tables of the app. */
exports.APP_BASE_THEME_VALUES = Object.fromEntries(exports.APP_BASE_THEME_NAMES.map(name => [
    name,
    Object.fromEntries(Object.entries(exports.APP_BASE_THEME_TOKEN_SOURCES).map(([token, role]) => [
        token,
        values_json_1.default[name][role],
    ])),
]));
/** Sizes and fonts every theme of the app shares. */
exports.APP_BASE_THEME_SHARED = {
    '--sl-font-sans': "'Inter', 'Source Sans 3', Poppins, apple-system, BlinkMacSystemFont, 'Helvetica Neue', Tahoma, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji','Segoe UI Symbol', 'Noto Color Emoji'",
    '--sl-font-serif': "'Source Serif 4', 'Noto Serif' , serif, 'Cambria', apple-system, BlinkMacSystemFont, 'Helvetica Neue', Tahoma, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji','Segoe UI Symbol', 'Noto Color Emoji'",
    '--sl-font-mono': "'Source Code Pro', 'IBM Plex Mono', 'Roboto Mono','Noto Sans Mono', 'Noto Sans CJK',  apple-system, BlinkMacSystemFont, 'Helvetica Neue', Tahoma, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji','Segoe UI Symbol', 'Noto Color Emoji'",
    '--sl-radius-s': '4px',
    '--sl-radius-m': '8px',
    '--sl-radius-l': '12px',
    '--sl-radius-round': '999px',
    '--sl-space-1': '4px',
    '--sl-space-2': '8px',
    '--sl-space-3': '12px',
    '--sl-space-4': '16px',
    '--sl-space-6': '24px',
    '--sl-space-8': '32px',
};
const block = (selector, values) => `${selector} {\n${Object.entries(values)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n')}\n}`;
const DESIGNED = `:root[${exports.APP_BASE_DESIGN_ATTRIBUTE}="streamlain"]`;
const themeBlock = (selector, name) => block(selector, {
    ...exports.APP_BASE_THEME_VALUES[name],
    'color-scheme': exports.APP_BASE_THEME_MODES[name],
});
/**
 * The stylesheet of the StreamLain design. It only speaks for a page marked
 * `data-sl-design="streamlain"`, and its selectors outweigh the `:root` of the
 * page, so an App can keep its own values for its own design on `:root`. The
 * theme is the one on `<html data-theme>`; before the host names one the page
 * follows the colour scheme of the device, as the app does for "system".
 */
function renderAppBaseThemeStylesheet() {
    return [
        block(DESIGNED, {
            ...exports.APP_BASE_THEME_SHARED,
            ...app_base_motion_1.APP_BASE_MOTION_TOKENS,
            ...exports.APP_BASE_THEME_VALUES.light,
            'color-scheme': 'light',
        }),
        `@media (prefers-color-scheme: dark) {\n${themeBlock(`${DESIGNED}:not([data-theme])`, 'dark')}\n}`,
        ...exports.APP_BASE_THEME_NAMES.filter(name => name !== 'light').map(name => themeBlock(`${DESIGNED}[data-theme="${name}"]`, name)),
    ].join('\n');
}
/**
 * A page of an App in the StreamLain design: the design stylesheet first in
 * its head, so its own styles can use the tokens, and the mark on `<html>`
 * the sheet speaks for. A page in its own design stays as it is. The
 * platform and the StreamKit deliver pages through this one function.
 */
function applyAppBaseDesign(html, design) {
    if (design !== 'streamlain')
        return html;
    const style = `<style data-sl-design-sheet>${renderAppBaseThemeStylesheet()}</style>`;
    return (0, app_base_html_document_1.insertIntoAppHead)((0, app_base_html_document_1.setAppHtmlAttribute)(html, exports.APP_BASE_DESIGN_ATTRIBUTE, 'streamlain'), style);
}
var app_base_theme_follow_1 = require("./app-base-theme-follow");
Object.defineProperty(exports, "installAppBaseThemeFollow", { enumerable: true, get: function () { return app_base_theme_follow_1.installAppBaseThemeFollow; } });
